import { lazy } from "react";

/**
 * lazy() wrapper that detects "failed to fetch dynamically imported module"
 * errors — the classic symptom of a user having a stale index.html
 * cached after a Vercel deploy (old chunk hashes no longer exist on
 * the CDN) — and force-reloads the page once to pick up the new build.
 *
 * Uses sessionStorage to guard against infinite reload loops: if we've
 * already tried to reload for this module once and it still fails,
 * re-throw so the ErrorBoundary can handle it.
 */
export function lazyWithRetry<T extends { default: React.ComponentType<any> }>(
  factory: () => Promise<T>,
  name?: string,
): React.LazyExoticComponent<T["default"]> {
  return lazy(async () => {
    const key = `fc_chunk_retry::${name ?? factory.toString().slice(0, 64)}`;
    try {
      return await factory();
    } catch (err) {
      const alreadyRetried = sessionStorage.getItem(key) === "1";
      const isChunkError =
        err instanceof Error &&
        /Failed to fetch dynamically imported module|Loading chunk|Importing a module script failed/i.test(err.message);

      if (isChunkError && !alreadyRetried) {
        sessionStorage.setItem(key, "1");
        // Hard reload to fetch the current index.html + chunk manifest.
        window.location.reload();
        // Return a never-resolving promise so React doesn't surface the error
        // during the tiny window before the reload actually happens.
        return new Promise<T>(() => {});
      }
      sessionStorage.removeItem(key);
      throw err;
    }
  });
}
